import { useEffect, useMemo, useState } from "react";
import { vendorApi } from "../services/vendorApi";
import FeedbackModal from "../components/ui/FeedbackModal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import VendorFormModal from "../components/vendors/VendorFormModal";

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
}

export default function Vendors() {
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    pendingReview: 0,
    highRisk: 0,
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [message, setMessage] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const openMessage = (type, text, title = type === "success" ? "Success" : "Error") => {
    setMessage({ open: true, type, title, message: text });
  };

  const closeMessage = () => setMessage((prev) => ({ ...prev, open: false }));

  const loadVendors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());

      const res = await vendorApi.list(params.toString() ? `?${params.toString()}` : "");
      setVendors(res.data?.vendors || []);
      setSummary(
        res.data?.summary || { total: 0, active: 0, pendingReview: 0, highRisk: 0 }
      );
      setSelectedIds([]);
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => loadVendors(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const allSelected = useMemo(
    () => vendors.length > 0 && selectedIds.length === vendors.length,
    [vendors.length, selectedIds.length]
  );

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(vendors.map((v) => v.id));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openCreate = () => {
    setEditingVendor(null);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await vendorApi.getById(id);
      setEditingVendor(res.data?.vendor || null);
      setFormOpen(true);
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to load vendor");
    }
  };

  const handleDelete = async () => {
    try {
      await vendorApi.remove(deleteId);
      setDeleteId(null);
      openMessage("success", "Vendor deleted successfully");
      loadVendors();
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to delete vendor");
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedIds.map((id) => vendorApi.remove(id)));
      setSelectedIds([]);
      openMessage("success", "Selected vendors deleted successfully");
      loadVendors();
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to delete selected vendors");
    }
  };

  const badgeClass = (status) => {
    const base =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize";
    if (status === "active") return `${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300`;
    if (status === "pending_review") return `${base} bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300`;
    if (status === "high_risk") return `${base} bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300`;
    return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  };

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100">
      <FeedbackModal
        open={message.open}
        type={message.type}
        title={message.title}
        message={message.message}
        onClose={closeMessage}
      />

      <ConfirmDialog
        open={deleteId !== null}
        title="Delete vendor"
        message="This vendor will be removed permanently."
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />

      <VendorFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(msg) => {
          openMessage("success", msg || "Vendor saved successfully");
          loadVendors();
        }}
        vendor={editingVendor}
      />

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            Vendors
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Manage suppliers, contacts, and vendor performance.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors..."
            className="w-72 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="pending_review">Pending Review</option>
            <option value="high_risk">High Risk</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={openCreate}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            + New Vendor
          </button>

          <button
            onClick={handleDeleteSelected}
            disabled={!selectedIds.length}
            className="rounded-xl border border-red-200 px-5 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            Delete Selected
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total Vendors" value={summary.total} />
        <StatCard label="Active Vendors" value={summary.active} />
        <StatCard label="Pending Review" value={summary.pendingReview} />
        <StatCard label="High Risk" value={summary.highRisk} />
      </div>

      <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Vendor Directory
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              View and manage all your vendors in one place.
            </p>
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Showing {vendors.length} vendors
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
                  />
                </th>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Services</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Spend</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Loading vendors...
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    No vendors found.
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => (
                  <tr
                    key={vendor.id}
                    className="bg-white transition hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(vendor.id)}
                        onChange={() => toggleSelectOne(vendor.id)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
                      />
                    </td>

                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        {vendor.name}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {vendor.email || vendor.vendorCode}
                      </div>
                    </td>

                    <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                      <div>{vendor.contactPerson || "-"}</div>
                      <div className="text-slate-500 dark:text-slate-400">
                        {vendor.phone || "-"}
                      </div>
                    </td>

                    <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {vendor.country || "-"}
                    </td>

                    <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {Array.isArray(vendor.services) && vendor.services.length
                        ? vendor.services.map((s) => (
                            <span
                              key={s}
                              className="mr-2 mb-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                            >
                              {s}
                            </span>
                          ))
                        : "-"}
                    </td>

                    <td className="px-4 py-4 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {Number(vendor.rating || 0).toFixed(1)}
                    </td>

                    <td className="px-4 py-4 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      £{Number(vendor.spend || 0).toFixed(2)}
                    </td>

                    <td className="px-4 py-4">
                      <span className={badgeClass(vendor.status)}>
                        {String(vendor.status || "inactive").replace(/_/g, " ")}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(vendor.id)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(vendor.id)}
                          className="rounded-xl border border-red-200 bg-white px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:bg-slate-900 dark:text-red-300 dark:hover:bg-red-950/40"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}